/**
 * Copy for the 10 service detail pages, extracted verbatim from the
 * design reference .dc.html files. Keyed by route slug.
 * Core services render at /services/:slug, specialized at /specialized-care/:slug.
 */
export const services = {
  'personal-care': {
    specialized: false,
    eyebrow: 'Services',
    title: 'Personal Care with Dignity',
    lede: 'Helping with the most private parts of the day requires trust and respect. Our caregivers provide gentle assistance with daily routines at a pace that feels comfortable for your loved one.',
    image: '/assets/personal-care.jpg',
    imageAlt: 'A caregiver offering a supportive hand to a senior',
    includedLede: "Personal care is about more than just tasks; it's about helping someone feel refreshed and ready for their day.",
    included: [
      'Bathing and showering assistance',
      'Dressing and wardrobe selection',
      'Grooming and hair care',
      'Oral hygiene and denture care',
      'Incontinence and toileting support',
      'Mobility and transfer assistance',
      'Medication reminders (non-administration)',
      'Morning and evening routines',
    ],
    cards: [
      {
        title: 'How we keep dignity first',
        body: 'We understand that needing help with personal tasks can be difficult. Our caregivers are trained to be discreet, patient, and to encourage as much independence as possible. We don’t "take over"; we support the person in doing what they can for themselves.',
      },
      {
        title: 'What stays with your healthcare team',
        body: 'Our care is strictly non-medical. While we provide reminders and mobility support, we do not administer medications, manage wounds, or perform medical assessments. Those critical tasks remain with your doctors and home care nurses.',
      },
    ],
    worksWellWith: [
      { label: 'Meal Preparation & Housekeeping', to: '/services/meals-housekeeping' },
      { label: 'Companionship & Outings', to: '/services/companionship' },
    ],
    faqs: [
      {
        q: 'Will we have the same caregiver every time?',
        a: 'We prioritize consistency. We aim to match you with a primary caregiver so that a trusting relationship can develop, which is especially important for personal care.',
      },
      {
        q: 'How do you handle privacy during bathing?',
        a: "Our caregivers are trained in 'towelled bathing' and other techniques that maintain modesty while ensuring safety and hygiene in the bathroom.",
      },
    ],
  },

  'meals-housekeeping': {
    specialized: false,
    eyebrow: 'Services',
    title: 'Meals & a Tidy Home',
    lede: 'A clean home and a warm meal are foundations of well-being. We help manage the daily chores that can become overwhelming, ensuring the home remains a place of comfort and health.',
    image: '/assets/meals.jpg',
    imageAlt: 'A caregiver and senior preparing a healthy meal together',
    includedLede: 'We focus on the tasks that make the biggest difference in daily comfort and safety.',
    included: [
      'Fresh meal preparation and cooking',
      'Washing dishes and kitchen cleanup',
      'Laundry, drying, and putting away',
      'Light dusting and vacuuming',
      'Changing bed linens and towels',
      'Emptying trash and recycling',
      'Monitoring food freshness in the fridge',
    ],
    cards: [
      {
        title: 'Focusing on nutrition and choice',
        body: "We don't use a fixed menu. Our caregivers cook the foods your loved one actually enjoys, following their recipes and dietary preferences. Whether it's a specific way of making tea or a favorite Sunday roast, we keep those traditions alive.",
      },
      {
        title: 'A safer, calmer environment',
        body: 'Clutter can be a trip hazard, and a messy kitchen can lead to health concerns. By keeping the home tidy and the fridge stocked with fresh food, we reduce the stress of daily living and create a safer environment for independent aging.',
      },
    ],
    worksWellWith: [
      { label: 'Personal Care', to: '/services/personal-care' },
      { label: 'Companionship & Outings', to: '/services/companionship' },
    ],
    faqs: [
      {
        q: 'Do you provide the groceries?',
        a: 'We cook with what is already in the pantry, and many families use a grocery delivery service to keep the kitchen stocked. You only pay for the cost of groceries; we never add a markup.',
      },
      {
        q: 'Is this a deep-cleaning service?',
        a: "We focus on 'light housekeeping' to maintain a tidy and safe environment. For heavy tasks like steam cleaning carpets or washing outside windows, we recommend a specialized cleaning company.",
      },
    ],
  },

  companionship: {
    specialized: false,
    eyebrow: 'Services',
    title: 'Connection & Companionship',
    lede: 'Loneliness can be the heaviest part of aging. Our caregivers provide the social connection, conversation, and shared activities that make every day feel more meaningful.',
    image: '/assets/companionship.jpg',
    imageAlt: 'A senior and caregiver enjoying a conversation over coffee',
    includedLede: 'We focus on building genuine relationships through shared interests and engagement.',
    included: [
      'Friendly conversation and listening',
      'Sharing hobbies like cards or puzzles',
      'Assistance with reading and mail',
      'Accompanying on neighborhood walks',
      'Engaging with family photos and memories',
      'Help with technology (tablets/video calls)',
      'Encouragement and emotional support',
    ],
    cards: [
      {
        title: 'Matching for personality',
        body: 'Companionship works best when there is a real connection. We look at interests, temperament, and life history to match caregivers who can actually share a joke, debate the news, or enjoy the same music as your loved one.',
      },
      {
        title: 'The bridge to the community',
        body: 'Isolation often happens when mobility or confidence decreases. Our caregivers act as a supportive bridge, helping seniors get back out into the Calgary community for the things they enjoy, always in a non-medical capacity.',
      },
    ],
    worksWellWith: [
      { label: 'Personal Care', to: '/services/personal-care' },
      { label: 'Meal Prep & Housekeeping', to: '/services/meals-housekeeping' },
    ],
    faqs: [
      {
        q: 'How do you choose activities for each visit?',
        a: "We follow your loved one's interests and energy that day — a card game at the table, looking through photo albums, a walk around the neighbourhood, or simply a good conversation over tea. Favourite activities are noted in the care plan and adjusted as preferences change.",
      },
      {
        q: 'What if my loved one is very introverted?',
        a: "We respect that. Companionship doesn't always have to be 'busy.' Sometimes it's just about having someone else in the house while they read, or a quiet presence to help with lunch.",
      },
    ],
  },

  'post-hospital-care': {
    specialized: false,
    eyebrow: 'Services',
    title: 'A steadier first few weeks home',
    lede: 'Extra non-medical hands during the first weeks home — meals, rest, routines and reminders, while medical care stays with your clinicians.',
    image: '/assets/post-hospital.jpg',
    imageAlt: 'A caregiver helping a senior settle in comfortably after a hospital stay',
    includedLede: 'We help bridge the gap between hospital discharge and daily independence, so recovery at home feels organized instead of overwhelming.',
    included: [
      'Help settling in after discharge',
      'Fresh meal preparation',
      'Medication reminders (non-administration)',
      'Mobility and transfer support',
      'Light housekeeping and laundry',
      'Changing bed linens and towels',
      'Recovery updates for the family',
    ],
    cards: [
      {
        title: 'Following the discharge plan',
        body: 'We follow the family-approved routines that support the discharge plan — rest schedules, meals, gentle activity and reminders — while doctors, nurses and therapists direct all medical care.',
      },
      {
        title: 'What stays with your healthcare team',
        body: 'Our support is strictly non-medical. Wound care, medication administration and clinical assessments remain with your hospital, home care nurses and physicians.',
      },
    ],
    worksWellWith: [
      { label: 'Personal Care', to: '/services/personal-care' },
      { label: 'Meal Prep & Housekeeping', to: '/services/meals-housekeeping' },
    ],
    faqs: [
      {
        q: 'How quickly can care start after discharge?',
        a: 'Contact us as early as possible — ideally before discharge — and we will prioritize scheduling so support is in place for the first days home.',
      },
      {
        q: 'Is this a medical service?',
        a: 'No. Post-hospital care from True North is non-medical support. Medical instructions and treatment remain the responsibility of qualified healthcare professionals.',
      },
    ],
  },

  dementia: {
    specialized: true,
    eyebrow: 'Specialized Care',
    title: 'Dignity and comfort for those with dementia',
    lede: 'We provide a calm, consistent presence that helps reduce confusion and anxiety, allowing your loved one to remain in the comfort of their familiar home.',
    image: '/assets/specialized.jpg',
    imageAlt: 'A caregiver providing patient support to a senior with dementia',
    includedLede: 'Our dementia care is focused on the individual—their history, their preferences, and their sense of safety.',
    included: [
      'Personalized routine building',
      'Gentle redirection & cueing',
      'Meaningful cognitive engagement',
      'Safety monitoring & supervision',
      'Mealtime support & hydration',
      'Assistance with personal hygiene',
      'Calming presence & companionship',
      'Regular family status updates',
    ],
    cards: [
      {
        title: 'Familiarity and Trust',
        body: "Consistency is the foundation of good dementia care. We match caregivers based on temperament and experience, ensuring they become a trusted part of your loved one's day.",
      },
      {
        title: 'Complementary Support',
        body: 'Our care is non-medical and designed to support the overall care plan managed by your family physician and specialists.',
      },
    ],
    worksWellWith: [
      { label: 'Respite Care', to: '/specialized-care/respite' },
      { label: 'Companionship', to: '/services/companionship' },
      { label: 'Personal Care', to: '/services/personal-care' },
    ],
    faqs: [
      {
        q: 'Do you use the same caregiver every time?',
        a: 'Yes, we prioritize consistency. For dementia care, we build a small, dedicated team of caregivers so your loved one can form a bond with familiar faces they trust.',
      },
      {
        q: 'How do you handle agitation or confusion?',
        a: 'Our caregivers use gentle techniques like redirection, validation, and maintaining a calm, low-stress environment to help manage difficult moments with dignity.',
      },
    ],
  },

  alzheimers: {
    specialized: true,
    eyebrow: 'Specialized Care',
    title: "Supporting the journey of Alzheimer's",
    lede: "We provide the steady, patient support needed to navigate the challenges of Alzheimer's, focusing on maintaining dignity and quality of life at home.",
    image: '/assets/specialized.jpg',
    imageAlt: 'A caregiver engaging in a memory-friendly activity with a senior',
    includedLede: "Our Alzheimer's care focuses on what is possible, celebrating small successes and maintaining a peaceful home environment.",
    included: [
      'Strict routine consistency',
      'Memory-friendly engagement',
      'Wandering prevention & safety',
      'Nutritious meal & snack support',
      'Gentle cues for daily activities',
      'Social connection & interaction',
      'Respite for family caregivers',
      'Detailed shift & mood notes',
    ],
    cards: [
      {
        title: 'Dignity First',
        body: 'We focus on the person, not the condition. By celebrating small wins and maintaining a calm, structured environment, we help seniors feel successful and safe.',
      },
      {
        title: 'Clear Boundaries',
        body: 'While we provide extensive emotional and practical support, medical assessments and clinical care remain the responsibility of your primary healthcare team.',
      },
    ],
    worksWellWith: [
      { label: 'Dementia Care', to: '/specialized-care/dementia' },
      { label: 'Respite Care', to: '/specialized-care/respite' },
      { label: 'Personal Care', to: '/services/personal-care' },
    ],
    faqs: [
      {
        q: 'Can you help if my loved one forgets to eat?',
        a: 'Yes. Our caregivers prepare familiar meals and provide gentle encouragement and reminders to ensure proper nutrition and hydration throughout the day.',
      },
      {
        q: 'How do we stay updated on their progress?',
        a: 'Detailed notes from every visit are available in our Family Room portal, covering activities, nutrition, mood, and any changes in routine.',
      },
    ],
  },

  palliative: {
    specialized: true,
    eyebrow: 'Specialized Care',
    title: 'Comfort, presence, and peace at home',
    lede: 'We provide the extra hands and quiet presence that allow families to focus on being together, while we handle the household and comfort needs.',
    image: '/assets/comfort-care.jpg',
    imageAlt: 'A caregiver providing quiet, comforting companionship',
    includedLede: 'Our palliative support is designed to wrap around your family, providing peace of mind and practical relief.',
    included: [
      'Comfort positioning assistance',
      'Quiet companionship & presence',
      'Mouth care & hydration support',
      'Light housekeeping for a calm space',
      'Favorite meal & fluid preparation',
      'Overnight respite for families',
      'Coordinating with the medical team',
      'Compassionate emotional support',
    ],
    cards: [
      {
        title: 'A Supportive Presence',
        body: 'We provide the steady, non-medical support that allows your family to step back from caregiving tasks and simply be present with your loved one.',
      },
      {
        title: 'Non-Medical Comfort',
        body: 'We work in harmony with your palliative nurses. While we provide comfort and companionship, we do not perform medical procedures or administer medication.',
      },
    ],
    worksWellWith: [
      { label: 'End-of-Life Care', to: '/specialized-care/end-of-life' },
      { label: 'Respite Care', to: '/specialized-care/respite' },
      { label: 'Personal Care', to: '/services/personal-care' },
    ],
    faqs: [
      {
        q: 'Do you provide nursing care?',
        a: 'No. Our role is to provide constant non-medical presence and comfort. Clinical tasks like pain management or medical procedures are handled by your palliative nursing team.',
      },
      {
        q: 'Can you be there overnight?',
        a: 'Yes. We can provide overnight support to ensure your loved one is never alone and to give family members the rest they need.',
      },
    ],
  },

  'end-of-life': {
    specialized: true,
    eyebrow: 'Specialized Care',
    title: 'Dignity and peace for the final days',
    lede: 'We provide a constant, quiet presence that ensures your loved one is never alone, while handling the practicalities of the home so your family can be together.',
    image: '/assets/comfort-care.jpg',
    imageAlt: 'A peaceful, comfortable home environment for end-of-life care',
    includedLede: 'Our focus is on creating a peaceful, dignified atmosphere and providing the constant support your family needs.',
    included: [
      'Constant overnight presence',
      'Quiet, dignified companionship',
      'Complete household management',
      'Non-medical comfort measures',
      'Essential respite for family',
      'Gentle personal hygiene support',
      'Coordination of visitors & family',
      'Steady emotional reassurance',
    ],
    cards: [
      {
        title: 'Being There Together',
        body: 'By handling the laundry, light meals, and quiet presence, we allow family members to focus on their emotional needs and their time with their loved one.',
      },
      {
        title: 'Working with Clinicians',
        body: 'Medical care remains the responsibility of your hospice or home care nursing team. We provide the continuous, non-medical support they cannot.',
      },
    ],
    worksWellWith: [
      { label: 'Palliative Care', to: '/specialized-care/palliative' },
      { label: 'Respite Care', to: '/specialized-care/respite' },
      { label: 'Companionship', to: '/services/companionship' },
    ],
    faqs: [
      {
        q: 'How quickly can care start?',
        a: 'We understand that end-of-life needs can change rapidly. We prioritize these requests and can often begin care within 24 to 48 hours of your assessment.',
      },
      {
        q: 'Can you stay with us in the hospital or hospice?',
        a: 'Yes. While we primarily serve seniors at home, we can also provide supplemental companionship and presence in hospital or hospice settings.',
      },
    ],
  },

  respite: {
    specialized: true,
    eyebrow: 'Specialized Care',
    title: 'Relief for the family caregivers',
    lede: 'Caring for a loved one is a marathon. Our respite services give you the time to rest, work, or travel, knowing your loved one is in safe, professional hands.',
    image: '/assets/respite.jpg',
    imageAlt: 'A family caregiver taking a well-deserved break',
    includedLede: 'Respite care is flexible and designed to fit into your existing routine, providing relief when you need it most.',
    included: [
      'Planned short-term relief',
      'Weekend & evening coverage',
      'Overnight care support',
      'Emergency back-up care',
      'Regular weekly break sessions',
      'Smooth transition support',
      'Detailed caregiver handovers',
    ],
    cards: [
      {
        title: 'Resting with Confidence',
        body: "Take the time you need to recharge your own batteries. Whether it's a few hours a week or a few days, we ensure your loved one's care never misses a beat.",
      },
      {
        title: 'Professional Hand-off',
        body: "We follow your established routines exactly. From favorite meals to specific comfort measures, we ensure a seamless transition for the senior while you're away.",
      },
    ],
    worksWellWith: [
      { label: 'Dementia Care', to: '/specialized-care/dementia' },
      { label: 'Personal Care', to: '/services/personal-care' },
      { label: 'Meal Prep & Housekeeping', to: '/services/meals-housekeeping' },
    ],
    faqs: [
      {
        q: 'Is there a minimum number of hours for respite?',
        a: "Yes, we typically have a minimum visit length to ensure stability for the senior and the caregiver. We'll discuss your specific needs during the assessment.",
      },
      {
        q: 'Do I need to meet the caregiver first?',
        a: 'Ideally, yes. We prefer to do an introduction so you feel confident leaving your loved one in our care and the senior feels comfortable with the new face.',
      },
    ],
  },
}
